import React, { useState, useEffect, useContext } from 'react';
import { AppContext } from '@/App';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { MapPin, Users, Plus, Check } from 'lucide-react';
import { BottomNav } from '@/components/BottomNav';
import { CreatePostModal } from '@/components/CreatePostModal';
import { Button } from '@/components/ui/button';

const CommunitiesPage = () => {
  const { user } = useContext(AppContext);
  const navigate = useNavigate();
  const [communities, setCommunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchCommunities();
  }, []);

  const fetchCommunities = async () => {
    try {
      const response = await axios.get('/communities');
      setCommunities(response.data);
    } catch (error) {
      toast.error('Failed to load communities');
    } finally {
      setLoading(false);
    }
  };

  const handleJoinLeave = async (communityId, isMember) => {
    try {
      if (isMember) {
        await axios.delete(`/communities/${communityId}/leave`);
        toast.success('Left community');
      } else {
        await axios.post(`/communities/${communityId}/join`);
        toast.success('Joined community!');
      }
      fetchCommunities();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update membership');
    }
  };

  const handleCommunityClick = (communityId) => {
    navigate(`/communities/${communityId}`);
  };

  const handleCreatePost = async (formData) => {
    setIsSubmitting(true);
    try {
      await axios.post('/ideas', formData);
      toast.success('Idea posted successfully!');
      setIsModalOpen(false);
    } catch (error) {
      toast.error('Failed to post idea');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-lg animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface pb-20 md:pb-8">
      {/* Header */}
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className="bg-white border-b border-cardBorder sticky top-0 z-40"
      >
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <MapPin className="w-6 h-6 text-primary" />
            <h1 className="text-2xl font-baloo font-bold text-primaryDark">Local Communities</h1>
          </div>
          <p className="text-sm text-gray mt-1">Join your city's community to discover local gems</p>
        </div>
      </motion.header>

      {/* Communities Grid */}
      <main className="max-w-6xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {communities.map((community, index) => (
            <motion.div
              key={community.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="bg-white rounded-lg border border-cardBorder shadow-sm hover:shadow-md transition-shadow overflow-hidden"
            >
              <div 
                onClick={() => handleCommunityClick(community.id)}
                className="p-5 cursor-pointer"
              >
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-12 h-12 bg-primary/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-primary-dark" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-baloo font-bold text-primaryDark text-lg leading-tight mb-1">
                      {community.city}
                    </h3>
                    <p className="text-xs text-grayMeta">
                      {community.state ? `${community.state}, ` : ''}{community.country}
                    </p>
                  </div>
                </div>
                
                <p className="text-sm text-gray leading-relaxed mb-4">
                  {community.description}
                </p>
                
                <div className="flex items-center justify-between">
                  <span className="text-xs text-grayMeta flex items-center gap-1">
                    <Users className="w-3 h-3" />
                    {community.member_count} {community.member_count === 1 ? 'member' : 'members'}
                  </span>
                </div>
              </div>
              
              {/* Join/Leave Button */}
              <div className="px-5 pb-4">
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleJoinLeave(community.id, community.is_member);
                  }}
                  className={`w-full h-10 rounded-lg font-semibold transition-colors ${
                    community.is_member
                      ? 'bg-voteDefault text-grayMeta hover:bg-voteDown-bg hover:text-voteDown-text border border-cardBorder'
                      : 'bg-primary text-primaryDark hover:bg-primary/90'
                  }`}
                >
                  {community.is_member ? (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Joined
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4 mr-2" />
                      Join Community
                    </>
                  )}
                </Button>
              </div>
            </motion.div>
          ))}
        </div>
      </main>

      {/* Bottom Nav */}
      <BottomNav onCreateClick={() => setIsModalOpen(true)} />

      {/* Create Post Modal */}
      <CreatePostModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleCreatePost}
        isLoading={isSubmitting}
      />
    </div>
  );
};

export default CommunitiesPage;
